import React, { useState, useCallback } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import { AccountCircle, ExitToApp, Settings } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { performLogout } from '../services/sessionExpiry';

/**
 * Top bar shared by every staff-facing admin page (AdminDashboard,
 * UserDetail): logo, title, ADMIN chip, and the avatar menu (User Dashboard /
 * System Admin / Logout). It was duplicated byte-for-byte across both pages;
 * this is the single copy.
 *
 * Deliberately self-contained rather than prop-driven: the title, menu items,
 * and navigation targets do not vary by page today, so there is nothing here
 * for a caller to parameterise. A page-specific breadcrumb (UserDetail has
 * one, AdminDashboard does not) renders separately, below this bar, inside
 * each page's own Container — moving it in here would change its width and
 * padding, which is a visual change this extraction must not make.
 */
function StaffAppBar() {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const username = localStorage.getItem('username');

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = useCallback(() => {
    performLogout(navigate);
  }, [navigate]);

  return (
    <AppBar position="static">
      <Toolbar>
        <Box
          component="img"
          sx={{
            height: 40,
            width: 40,
            mr: 2,
          }}
          alt="FiStone Logo"
          src="/fistone-logo.png"
        />
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          FiStone Admin Dashboard
        </Typography>
        <Chip
          label="ADMIN"
          color="warning"
          size="small"
          sx={{ mr: 2 }}
        />
        <div>
          <IconButton
            size="large"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleMenu}
            color="inherit"
          >
            <Avatar sx={{ bgcolor: 'secondary.main' }}>
              {username ? username[0].toUpperCase() : <AccountCircle />}
            </Avatar>
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem disabled>
              <Typography variant="body2">{username}</Typography>
            </MenuItem>
            <MenuItem onClick={() => navigate('/dashboard')}>
              User Dashboard
            </MenuItem>
            <MenuItem onClick={() => navigate('/system-admin')}>
              <Settings sx={{ mr: 1 }} /> System Admin
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ExitToApp sx={{ mr: 1 }} /> Logout
            </MenuItem>
          </Menu>
        </div>
      </Toolbar>
    </AppBar>
  );
}

export default StaffAppBar;
